import { Link } from "react-router-dom";
import { Package, Archive, Truck, Check } from "lucide-react";

const PricingPage = () => {
  const pricingTiers = [
    {
      icon: Package,
      title: "Pallet Storage",
      subtitle: "Standard Storage Solution",
      price: null,
      priceLabel: "Contact for rates",
      features: [
        "Using Pallets Dimensions of 1000mm x 1200mm x 1000mm",
        "Frees up your own warehouse/storage space",
        "3rd Party Logistics & Order Fulfilment available",
      ],
      highlight: false,
      image: "https://images.pexels.com/photos/10096900/pexels-photo-10096900.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    },
    {
      icon: Archive,
      title: "Document / Deep Pallet Storage",
      subtitle: "High Capacity Solution",
      price: "£2.50",
      priceLabel: "+ VAT per pallet per week",
      features: [
        "Pallet storage rates based on 1000mm x 1200mm x 1000mm in high pallet locations",
        "Inbound Pallet Charge inc. shrink wrapping: £3.00 + VAT per pallet",
        "£5.00 + VAT for a new physical pallet",
        "Outbound Pallet Charge: £3.00 + VAT per pallet",
      ],
      highlight: true,
      badge: "Most Popular",
      image: "https://images.pexels.com/photos/10096887/pexels-photo-10096887.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    },
    {
      icon: Truck,
      title: "Third Party Logistics",
      subtitle: "Full Service Solution",
      price: null,
      priceLabel: "Custom quote",
      description: "Our logistics team can provide solutions to support your company when outsourcing your logistics services. We can efficiently pick and pack your goods to ensure that you meet your deadlines and customer's needs.",
      features: [
        "Picking & Packing",
        "Re-labelling",
        "Ship B2B/direct to customer",
        "Amazon FBA Preparation and Shipping",
        "E-commerce Shipping integration into all major marketplaces",
      ],
      platforms: "Amazon PRIME, eBay, TESCO, The Range, Etsy, B&Q, OnBuy, TikTok and your own Websites",
      highlight: false,
      image: "https://images.pexels.com/photos/8760709/pexels-photo-8760709.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    },
  ];

  return (
    <div data-testid="pricing-page">
      {/* Header Section */}
      <section className="bg-white py-24 md:py-32" data-testid="pricing-header">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center max-w-3xl mx-auto">
            <p className="text-sm font-semibold tracking-wider uppercase text-orange-500 mb-4">
              Our Pricing
            </p>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-extrabold text-slate-900 tracking-tight leading-tight mb-6">
              Transparent Pricing
            </h1>
            <p className="text-base md:text-lg text-slate-600 leading-relaxed">
              Choose the storage solution that fits your business needs. All prices exclude VAT.
            </p>
          </div>
        </div>
      </section>

      {/* Pricing Cards */}
      <section className="bg-slate-50 py-24 md:py-32" data-testid="pricing-cards">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {pricingTiers.map((tier, index) => (
              <div
                key={index}
                className={`bg-white border p-8 transition-all duration-300 hover:shadow-xl flex flex-col ${
                  tier.highlight 
                    ? "ring-2 ring-orange-500 border-orange-500 relative" 
                    : "border-slate-100 hover:border-orange-500/30"
                }`}
                data-testid={`pricing-card-${index}`}
              >
                {tier.badge && (
                  <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-orange-500 text-white text-xs font-bold px-4 py-1 uppercase tracking-wider">
                    {tier.badge}
                  </div>
                )}

                {/* Card Image */}
                <div className="h-40 mb-6 overflow-hidden">
                  <img
                    src={tier.image}
                    alt={tier.title}
                    className="w-full h-full object-cover"
                  />
                </div>

                {/* Icon & Title */}
                <div className="mb-6">
                  <tier.icon className={`w-10 h-10 mb-4 ${tier.highlight ? 'text-orange-500' : 'text-slate-400'}`} />
                  <h3 className="font-heading text-xl font-bold text-slate-900 mb-1">
                    {tier.title}
                  </h3>
                  <p className="text-sm text-slate-500">{tier.subtitle}</p>
                </div>

                {/* Price */}
                <div className="mb-6">
                  {tier.price ? (
                    <div>
                      <span className="font-heading text-4xl font-extrabold text-slate-900">
                        {tier.price}
                      </span>
                      <p className="text-sm text-slate-500 mt-1">{tier.priceLabel}</p>
                    </div>
                  ) : (
                    <p className="text-lg font-semibold text-orange-500">{tier.priceLabel}</p>
                  )}
                </div>

                {/* Description */}
                {tier.description && (
                  <p className="text-sm text-slate-600 mb-4 leading-relaxed">
                    {tier.description}
                  </p>
                )}

                {/* Features */}
                <ul className="space-y-3 mb-8 flex-1">
                  {tier.features.map((feature, fIndex) => (
                    <li key={fIndex} className="flex items-start gap-3">
                      <Check className="w-5 h-5 text-orange-500 flex-shrink-0 mt-0.5" />
                      <span className="text-sm text-slate-600">{feature}</span>
                    </li>
                  ))}
                </ul>

                {/* Platforms (for 3PL) */}
                {tier.platforms && (
                  <div className="mb-8 p-4 bg-slate-50 border border-slate-100">
                    <p className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">
                      Supported Platforms
                    </p>
                    <p className="text-xs text-slate-600">{tier.platforms}</p>
                  </div>
                )}

                {/* CTA */}
                <Link
                  to="/contact"
                  className={`text-center font-bold py-4 px-6 transition-all duration-200 ${
                    tier.highlight
                      ? "bg-orange-500 hover:bg-orange-600 text-white shadow-sm hover:shadow-md"
                      : "bg-white border border-slate-200 text-slate-900 hover:border-orange-500 hover:text-orange-500"
                  }`}
                  data-testid={`pricing-cta-${index}`}
                >
                  Get a Quote
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Additional Info */}
      <section className="bg-white py-24 md:py-32" data-testid="pricing-info">
        <div className="max-w-7xl mx-auto px-6 md:px-12 text-center">
          <h2 className="font-heading text-3xl md:text-4xl font-bold text-slate-900 tracking-tight mb-4">
            Need a Custom Solution?
          </h2>
          <p className="text-slate-600 text-base md:text-lg mb-8 max-w-2xl mx-auto">
            We understand every business is different. Contact us to discuss your specific requirements and we'll create a tailored package for you.
          </p>
          <Link
            to="/contact"
            className="inline-block bg-orange-500 hover:bg-orange-600 text-white font-bold py-4 px-8 transition-all duration-200 shadow-sm hover:shadow-md"
            data-testid="pricing-custom-cta"
          >
            Get in Touch
          </Link>
        </div>
      </section>
    </div>
  );
};

export default PricingPage;
